<script lang="ts" setup>
import countTo from 'vue-count-to'
import { computed, ref, watch } from 'vue'
import type { MemberTopupSubscribeProduct, SubscribePageConfig } from '@/composables/payment'
import {
  createShowPaymentMethodSelector,
  useSubscription,
} from '@/composables/payment'
import { PaymentMethodEnum } from '@/composables/payment/constants'
import { useAxonViewItem } from '@/composables/axon/useAxonViewItem'
import { useFbqAddToCart } from '@/composables/fbq/useFbqAddToCart'
import { getDailyPrice, getSavePercent, getUnitPriceValue, shouldShowDailyPrice } from '@/composables/payment/utils/subscribeDailyPrice'
import { VIP_TYPE } from '@/constants'
import paypalIcon from '@/assets/paypal.png'
import stripeIcon from '@/assets/stripe.svg'
import paypalDarkIcon from '@/assets/paypal-dark.png'
import stripeDarkIcon from '@/assets/stripe-dark.svg'

const props = defineProps<{
  config: SubscribePageConfig
}>()

const {
  subscribeProducts,
  getUnitPrice,
  onSubscribeProducts,
  isZero,
  currentPaymentMethod,
  availablePaymentMethods,
  selectPaymentMethod,
} = useSubscription('store')

const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

const { reportViewItem } = useAxonViewItem()
watch(subscribeProducts, (val) => {
  if (val.length)
    reportViewItem(val, currentPaymentMethod.value, 'Membership', p => p.preferentialPrice ?? p.price)
})

const { reportAddToCart } = useFbqAddToCart()
watch(subscribeProducts, (val) => {
  for (const p of val)
    reportAddToCart(p.productId, 'Membership', p.preferentialPrice ?? p.price ?? 0)
})

const active = ref(0)

const paymentMethods = computed(() => {
  return (availablePaymentMethods.value || []).map((method: any) => {
    const paymentMethod = Number(method.paymentMethod)
    const isDark = props.config.darkPaymentSelector
    return {
      value: paymentMethod,
      icon: paymentMethod === PaymentMethodEnum.PAYPAL
        ? (isDark ? paypalDarkIcon : paypalIcon)
        : (isDark ? stripeDarkIcon : stripeIcon),
      alt: paymentMethod === PaymentMethodEnum.PAYPAL ? 'PayPal' : 'Stripe',
    }
  })
})

// 默认配色方案
const defaultColorSchemes = {
  discounted: {
    bg: 'linear-gradient(270deg, #FFE1E4 0%, #FFD9DF 100%)',
    priceBg: 'linear-gradient(90deg, #FF7FED 0%, #FF516B 100%)',
    titleColor: '#77382C',
    descColor: '#A96D61',
    borderColor: 'transparent',
    unitPriceColor: '#A96D61',
    originalPriceColor: '#A96D61',
    cycleLabelBg: 'linear-gradient(90deg, #FFC2CB 0%, #FECCD3 100%)',
    cycleLabelColor: '#A96D61',
  },
  normal: {
    bg: 'linear-gradient(90deg, #E9DBFF 0%, #EEE3FF 100%)',
    priceBg: 'linear-gradient(90deg, #C77CFF 0%, #6773F3 100%)',
    titleColor: '#3D426E',
    descColor: '#797CA0',
    borderColor: 'transparent',
    unitPriceColor: '#797CA0',
    originalPriceColor: '#797CA0',
    cycleLabelBg: 'linear-gradient(270deg, #E9DBFF 0%, #DEE2FE 100%)',
    cycleLabelColor: '#3D426E',
  },
  fakeDiscount: {
    bg: 'linear-gradient(270deg, #FFECDE 0%, #FFDFB8 100%)',
    priceBg: 'linear-gradient(90deg, #F7A82A 0%, #FE7E01 100%)',
    titleColor: '#77382C',
    descColor: '#9E7739',
    borderColor: 'transparent',
    unitPriceColor: '#9E7739',
    originalPriceColor: '#9E7739',
    cycleLabelBg: 'linear-gradient(270deg, #FFD1AE 0%, #FFD5A2 100%)',
    cycleLabelColor: '#775217',
  },
}

// 页面级可配置颜色
const defaultPageColors = {
  pageBg: '#fdf2f8',
  paymentCardBg: '#fff',
  paymentCardBorder: '#e5e7eb',
  paymentCardActiveBorder: '#ec3a9c',
  tagGradient: 'linear-gradient(90deg, #FF4754 0%, #FEA94C 100%)',
}

const pageColors = computed(() => {
  return { ...defaultPageColors, ...props.config.colors }
})

/**
 * 获取卡片类型
 * discounted: 有优惠价（preferentialPrice != null）
 * normal: 畅读无优惠（preferentialPrice == null 且畅读会员）
 * fakeDiscount: 折扣假折扣（preferentialPrice == null 且折扣会员且 discountOriginalPrice > 0）
 */
function getCardType(item: MemberTopupSubscribeProduct): 'discounted' | 'normal' | 'fakeDiscount' {
  if (item.preferentialPrice !== undefined && item.preferentialPrice !== null)
    return 'discounted'

  if (item.subscribeType === VIP_TYPE.DISCOUNT && item.discountOriginalPrice && Number(item.discountOriginalPrice) > 0)
    return 'fakeDiscount'

  return 'normal'
}

function getColorScheme(item: MemberTopupSubscribeProduct) {
  const configSchemes = props.config.colors?.schemes
  const cardType = getCardType(item)
  if (configSchemes && configSchemes[cardType])
    return configSchemes[cardType]

  return defaultColorSchemes[cardType]
}

function getCycleLabel(item: MemberTopupSubscribeProduct) {
  if (item.subscribeType === VIP_TYPE.DISCOUNT)
    return `${item.cycleStr} Subscription`
  return item.cycleStr
}
</script>

<template>
  <div class="storlyn-subscribe-page" :style="{ background: pageColors.pageBg }">
    <section v-if="showPaymentMethodSelector" class="storlyn-section storlyn-section-first">
      <h2 class="storlyn-section-title" :style="{ color: pageColors.titleColor || '#1f2937' }">
        PAYMENT METHOD
      </h2>
      <div class="payment-method-grid">
        <button
          v-for="method in paymentMethods"
          :key="method.value"
          type="button"
          class="payment-method-card"
          :class="{ active: Number(currentPaymentMethod) === Number(method.value) }"
          :style="{
            background: pageColors.paymentCardBg,
            borderColor: Number(currentPaymentMethod) === Number(method.value)
              ? pageColors.paymentCardActiveBorder
              : pageColors.paymentCardBorder,
          }"
          @click="selectPaymentMethod(method.value)"
        >
          <img :src="method.icon" :alt="method.alt" class="payment-method-icon">
        </button>
      </div>
    </section>

    <div class="storlyn-content">
      <h2 class="storlyn-section-title" :style="{ color: pageColors.titleColor || '#1f2937' }">
        SUBSCRIPTION
      </h2>
      <div
        v-for="(item, index) in subscribeProducts"
        :key="index"
      >
        <div
          v-if="item.isShow"
          class="storlyn-card"
          :class="{ active: active === index }"
          :style="{
            background: getColorScheme(item).bg,
            borderColor: getColorScheme(item).borderColor,
          }"
          @click="() => { active = index; onSubscribeProducts(item); }"
        >
          <!-- 角标 -->
          <div
            v-if="item.cornerTip === 1 && item.marketingCopy"
            class="storlyn-popular-tag"
            :style="{ background: pageColors.tagGradient }"
          >
            {{ item.marketingCopy }}
          </div>

          <!-- 周期标签（仅折扣会员显示） -->
          <div
            v-if="item.subscribeType === VIP_TYPE.DISCOUNT"
            class="storlyn-cycle-label"
            :style="{
              background: getColorScheme(item).cycleLabelBg,
              color: getColorScheme(item).cycleLabelColor,
            }"
          >
            {{ getCycleLabel(item) }}
          </div>

          <!-- 折扣会员 -->
          <template v-if="item.subscribeType === VIP_TYPE.DISCOUNT">
            <div class="storlyn-card-content">
              <div class="storlyn-card-left">
                <div class="storlyn-coins-title" :style="{ color: getColorScheme(item).titleColor }">
                  {{ item.discountCoins }} coins
                </div>
                <ul class="storlyn-coins-list">
                  <li class="storlyn-coins-item" :style="{ color: getColorScheme(item).descColor }">
                    <span class="storlyn-dot" :style="{ background: getColorScheme(item).descColor }" />
                    <span>{{ config.texts.payTemplateGetCoins?.(item.discountOnceCoins) }}</span>
                  </li>
                  <li class="storlyn-coins-item" :style="{ color: getColorScheme(item).descColor }">
                    <span class="storlyn-dot" :style="{ background: getColorScheme(item).descColor }" />
                    <span>{{ config.texts.payTemplateEarnCoins?.(item.discountBonusCoins, item.discountDailyCoins) }}</span>
                  </li>
                </ul>
              </div>
              <div class="storlyn-card-right">
                <div class="storlyn-price-btn" :style="{ background: getColorScheme(item).priceBg }">
                  $<countTo
                    :start-val="Number(item.discountOriginalPrice)"
                    :end-val="Number(item.preferentialPrice || item.price)"
                    :duration="3000"
                    :decimals="2"
                  />
                </div>
                <div class="storlyn-original-price" :style="{ color: getColorScheme(item).originalPriceColor }">
                  $ {{ item.discountOriginalPrice }}
                </div>
              </div>
            </div>
          </template>

          <!-- 畅读会员 -->
          <template v-else>
            <div class="storlyn-card-content">
              <div class="storlyn-card-left">
                <div class="storlyn-title-row">
                  <div class="storlyn-coins-title" :style="{ color: getColorScheme(item).titleColor }">
                    {{ item.cycleStr }}
                  </div>
                  <div
                    v-if="shouldShowDailyPrice(item, isZero)"
                    class="storlyn-save-tag"
                  >
                    SAVE {{ getSavePercent(item) }}%
                  </div>
                </div>
                <div class="storlyn-subscribe-desc" :style="{ color: getColorScheme(item).descColor }">
                  {{ item.subscribeText }}
                </div>
              </div>
              <div class="storlyn-card-right">
                <template v-if="isZero(item.preferentialPrice)">
                  <div class="storlyn-price-btn" :style="{ background: getColorScheme(item).priceBg }">
                    {{ config.texts.free }}
                  </div>
                  <div class="storlyn-original-price" :style="{ color: getColorScheme(item).originalPriceColor }">
                    $ {{ item.price }}
                  </div>
                </template>
                <template v-else-if="!item.preferentialPrice">
                  <div class="storlyn-price-btn" :style="{ background: getColorScheme(item).priceBg }">
                    <template v-if="getUnitPriceValue(item)">
                      ${{ getUnitPriceValue(item) }}<span class="storlyn-unit-suffix"> / Day</span>
                    </template>
                    <template v-else>
                      $ {{ item.price }}
                    </template>
                  </div>
                  <div v-if="getUnitPriceValue(item)" class="storlyn-unit-price" :style="{ color: getColorScheme(item).unitPriceColor }">
                    $ {{ item.price }}
                  </div>
                </template>
                <template v-else>
                  <div class="storlyn-price-btn" :style="{ background: getColorScheme(item).priceBg }">
                    <template v-if="shouldShowDailyPrice(item, isZero)">
                      ${{ getDailyPrice(item) }}<span class="storlyn-unit-suffix"> / Day</span>
                    </template>
                    <template v-else>
                      $<countTo
                        :start-val="Number(item.price)"
                        :end-val="Number(item.preferentialPrice)"
                        :duration="3000"
                        :decimals="2"
                      />
                    </template>
                  </div>
                  <div v-if="shouldShowDailyPrice(item, isZero)" class="storlyn-price-row">
                    <span class="storlyn-line-through" :style="{ color: getColorScheme(item).originalPriceColor }">$ {{ item.price }}</span>
                    <span class="storlyn-total-price" :style="{ color: getColorScheme(item).titleColor }">$ {{ item.preferentialPrice }}</span>
                  </div>
                  <div v-else class="storlyn-original-price" :style="{ color: getColorScheme(item).originalPriceColor }">
                    $ {{ item.price }}
                  </div>
                </template>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Tips -->
      <div class="storlyn-tips">
        <p class="storlyn-tips-title">
          {{ config.texts.tips }}
        </p>
        <p class="storlyn-tips-item">
          {{ config.texts.tip1 }}
        </p>
        <p class="storlyn-tips-item">
          {{ config.texts.tip2 }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.storlyn-subscribe-page {
  min-height: 100vh;
  padding-bottom: 100px;
}

.storlyn-section {
  padding: 0 20px;
}

.storlyn-section-first {
  margin-top: 12px;
}

.storlyn-section-title {
  margin: 0 0 12px;
  font-size: 15px;
  line-height: 1.2;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.payment-method-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.payment-method-card {
  height: 48px;
  border-radius: 7px;
  border: 1px solid;
  box-shadow: 0 8px 24px rgba(31, 41, 55, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.payment-method-card.active {
  box-shadow: 0 10px 26px rgba(142, 71, 255, 0.18);
}

.payment-method-icon {
  width: 100%;
  max-width: 128px;
  height: 28px;
  object-fit: contain;
}

.payment-method-icon[src*='stripe'] {
  height: 28px;
  padding: 4px 0;
}

.storlyn-content {
  margin-top: 22px;
  padding: 0 20px;
}

.storlyn-card {
  position: relative;
  padding: 0 14px;
  border-radius: 8px;
  border: 0;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
  height: 92px;
  display: flex;
  align-items: center;
}

.storlyn-card.active {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.storlyn-popular-tag {
  position: absolute;
  top: 0;
  left: 0;
  min-width: 49px;
  height: 16px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  border-radius: 8px 0 8px 0;
  font-weight: 500;
  font-size: 10px;
  color: #FFFFFF;
}

.storlyn-cycle-label {
  position: absolute;
  top: 0;
  right: 0;
  width: 117px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0 8px 0 8px;
  font-size: 10px;
  font-weight: normal;
  letter-spacing: -0.1666666716337204px;
  font-family: HelveticaNeue;
  opacity: 0.9;
}

.storlyn-card-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.storlyn-card-left {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.storlyn-card-right {
  flex-shrink: 0;
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 5px;
}

.storlyn-coins-title {
  font-size: 18px;
  font-weight: 500;
  font-family: HelveticaNeue-Medium;
  line-height: 1;
  margin-bottom: 0;
  white-space: nowrap;
}

.storlyn-coins-list {
  margin: 5px 0 0;
  padding: 0;
  list-style: none;
}

.storlyn-coins-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  line-height: 14px;
  font-family: HelveticaNeue;
  margin-bottom: 2px;
}

.storlyn-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  flex-shrink: 0;
  margin: 6px 0 4px 0;
}

.storlyn-subscribe-desc {
  font-size: 14px;
  margin-top: 8px;
  line-height: 14px;
  font-family: HelveticaNeue;
}

.storlyn-price-btn {
  width: 81px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  font-size: 14px;
  color: #FFFFFF;
  font-weight: 500;
  font-family: Inter-Medium;
}

.storlyn-original-price {
  font-size: 14px;
  text-decoration: line-through;
  margin-top: 4px;
  font-family: HelveticaNeue;
  text-align: center;
  width: 100%;
}

.storlyn-unit-price {
  font-size: 12px;
  margin-top: 4px;
  font-family: HelveticaNeue;
  text-align: center;
  width: 100%;
}

.storlyn-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.storlyn-save-tag {
  background: rgba(247, 73, 132, 0.15);
  color: rgba(247, 73, 132, 1);
  font-size: 10px;
  font-weight: 500;
  border-radius: 4px;
  padding: 3px 6px 4px;
  line-height: 8px;
  white-space: nowrap;
}

.storlyn-unit-suffix {
  font-size: 10px;
  font-weight: 400;
  opacity: 0.85;
}

.storlyn-price-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 4px;
}

.storlyn-line-through {
  font-size: 14px;
  text-decoration: line-through;
  font-family: HelveticaNeue;
  text-align: center;
}

.storlyn-total-price {
  font-size: 14px;
  font-weight: 500;
  font-family: HelveticaNeue;
  text-align: center;
}

.storlyn-tips {
  margin-top: 24px;
  padding: 16px 4px 100px;
}

.storlyn-tips-title {
  font-size: 12px;
  font-weight: normal;
  color: #9296A0;
  margin-bottom: 6px;
  font-family: HelveticaNeue;
}

.storlyn-tips-item {
  font-size: 11px;
  color: #9296A0;
  margin-top: 6px;
  line-height: 17px;
  font-family: HelveticaNeue;
  letter-spacing: 0.2199999988079071px;
}
</style>
